import { Button, Field, FormInput, IconNotificationSuccess, tokens, utils } from '@snsw/react-component-library';
import { GelTheme } from 'components/GelTheme';
import { useOptionallyControlledValue } from 'components/Utils/useOptionallyControlledValue';
import type { ContactDetailType, ContactMethodVerificationStatus } from 'components/VerifiableContactDetailField/types';
import { VerifiableContactDetail } from 'components/VerifiableContactDetailField/types';
import { VerifiableContactDetailApi } from 'components/VerifiableContactDetailField/components/VerifiableContactDetailApi';
import { VerifiableContactDetailModal } from 'components/VerifiableContactDetailField/components/VerifiableContactDetailModal';
import { ChangeEvent, forwardRef, ReactNode, useState } from 'react';
import { useBoolean } from 'react-use';
import styled, { css } from 'styled-components';
import classes from 'components/VerifiableContactDetailField/components/VerifiableContactDetailField.module.scss';

export interface VerifiableContactDetailFieldInputProps {
  label: string;
  helpMessage?: string | ReactNode;
  placeholder?: string;
  className?: string;
  prefix?: string;
  inputWidth?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface VerifiableContactDetailFieldButtonProps {
  label: string;
  className?: string;
}

export interface VerifiableContactDetailFieldBaseProps {
  id?: string;
  name: string;
  className?: string;
  /** Marks the control as aria-required */
  required?: boolean;
  /** Marks the control as aria-optional */
  optional?: boolean;
  failuresBeforeSkip?: number;
  value: string;

  hasError?: boolean;
  errorMessage?: string;

  /** The API to use for sending and verifying codes */
  api: VerifiableContactDetailApi;
}

export interface VerifiableContactDetailFieldProps extends VerifiableContactDetailFieldBaseProps {
  type: ContactDetailType;
  inputProps: VerifiableContactDetailFieldInputProps;
  buttonProps: VerifiableContactDetailFieldButtonProps;
  disabled?: boolean;

  onChange: (next: VerifiableContactDetail) => void;
  onBlur?: (current: VerifiableContactDetail) => void;
  enableVerify: boolean;

  normalize: (contactDetail: string) => string;

  verifiedContactDetails?: string[];
  defaultVerifiedContactDetails?: string[];
  onVerifiedContactDetailsChange?: (next: string[]) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${GelTheme.spacing(1)};
`;

const IconNotificationContainer = styled.div`
  padding: 20px 0 0;
`;

const VerifyButton = styled(Button)`
  margin-top: ${GelTheme.spacing(0.5)};
`;

const ErrorMessage = styled(Field.Error)`
  padding-top: 2px;
`;

// Kiama's FormInput control applies the input width to the input element, but then wraps that in a flexbox which does
// not size properly unless it's the only element in its containing flexbox. Since we are putting a button (or an icon)
// next to the FormInput, the flexbox does not expand correctly. As such, we need to apply whatever the default width
// is for the text box (based on the 'inputWidth' property) as the flex-basis for its containing flexbox.
const StyledFormInput = styled(FormInput)<Pick<VerifiableContactDetailFieldInputProps, 'inputWidth'>>`
  && {
    margin-top: 4px;
    ${({ inputWidth }) => {
      if (!inputWidth) return undefined;

      const inputWidthPx = tokens.layouts.inputWidth[inputWidth];
      return inputWidthPx
        ? css`
            flex-basis: ${utils.pxToRem(inputWidthPx)};
          `
        : undefined;
    }};
  }
`;

// We need to place our own label and help message above the FormInput (rather than using the corresponding properties
// on FormInput) since the ones from FormInput have their width constrained to the width of the input box, but we want
// the width to be constrained by the width of our whole component.
const Label = styled.label`
  font-weight: ${GelTheme.fontWeight('semi-bold')};
  color: ${GelTheme.color('blackBlue')};
`;

const HelpMessage = styled.span`
  color: ${GelTheme.color('shuttle')};
  font-size: 0.875rem;
`;

// Create a map of normalized values to the original value.
const getVerifiedMap = (source: string[], normalize: (contactDetail: string) => string) => () => {
  if (!(source?.length > 0)) return new Map<string, string>();

  const filteredPairs: [string, string][] = source
    .map((value) => {
      const valueNormalized = value?.length > 0 ? normalize(value) : '';
      return valueNormalized?.length > 0 ? ([valueNormalized, value] as [string, string]) : undefined;
    })
    .filter((pair) => !!pair);

  return new Map(filteredPairs);
};

export const VerifiableContactDetailField = forwardRef<HTMLInputElement, VerifiableContactDetailFieldProps>(
  (props, ref) => {
    const {
      id,
      name,
      type,
      className,
      inputProps,
      buttonProps,

      required,
      optional,

      failuresBeforeSkip,
      enableVerify,

      value,
      onChange,
      onBlur,

      normalize,
      verifiedContactDetails,
      defaultVerifiedContactDetails,
      onVerifiedContactDetailsChange,

      hasError,
      errorMessage,
      disabled,

      api,
    } = props;

    // Map of normalized values to the original value, based on either a controlled value (verifiedContactDetails)
    // or an uncontrolled initial value (defaultVerifiedContactDetails).
    const [verified, setVerified] = useOptionallyControlledValue(
      getVerifiedMap(defaultVerifiedContactDetails, normalize),
      verifiedContactDetails != null ? getVerifiedMap(verifiedContactDetails, normalize) : undefined,
      (next) => onVerifiedContactDetailsChange && onVerifiedContactDetailsChange(Array.from(next.values()))
    );

    const addVerified = (normalized: string, original: string) => {
      if (verified.has(normalized)) return;

      const next = new Map(verified);
      next.set(normalized, original);
      setVerified(next);
    };

    const getStatus = (contactDetail: string) => {
      return verified.has(normalize(contactDetail)) ? ('verified' as ContactMethodVerificationStatus) : 'unverified';
    };

    const status = getStatus(value);

    const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;

      onChange({
        value: next,
        status: getStatus(next),
      });
    };

    const handleBlur = () => {
      onBlur &&
        onBlur({
          value,
          status: getStatus(value),
        });
    };

    // For sending the code, we need to add the prefix (to cater for mobile numbers) and remove all spaces (which
    // should be harmless for both mobiles and email addresses).
    const prefixedValue = `${inputProps.prefix || ''}${value}`.replace(/\s/g, '');

    const [showVerifyModal, toggleVerifyModal] = useBoolean(false);
    const [codeSentTo, setCodeSentTo] = useState<string | undefined>(undefined);
    const [failuresCount, setFailuresCount] = useState(0);

    const handleCodeSent = () => {
      setFailuresCount(0);
      setCodeSentTo(value);
    };

    const handleVerifyCodeFailed = () => setFailuresCount((prev) => prev + 1);

    const handleVerifyComplete = (
      status: ContactMethodVerificationStatus,
      response?: { hash?: string; verifiedDetail?: string }
    ) => {
      toggleVerifyModal();

      if (status === 'verified') {
        addVerified(normalize(value), value);
      }
      // Pass the original value of the generated hash as the 'value' property changes as per display requirements.
      onChange({ value, status, hash: response?.hash, verifiedDetail: response?.verifiedDetail });
    };

    const { label, helpMessage, ...inputPropsRest } = inputProps;
    const { label: verifyButtonLabel, ...buttonPropsRest } = buttonProps;

    const showVerifyButton = enableVerify && status !== 'verified';

    return (
      <Container className={className}>
        <Label htmlFor={id ?? name}>{inputProps.label}</Label>
        <HelpMessage>{inputProps.helpMessage}</HelpMessage>

        <InputContainer>
          <StyledFormInput
            id={id ?? name}
            name={name}
            value={value}
            onChange={handleValueChange}
            onBlur={handleBlur}
            hasError={hasError}
            errorMessage={hasError ? '' : undefined}
            isRequired={required}
            isOptional={optional}
            className={classes.formInput}
            inputRef={ref}
            margin={{ top: 'none' }}
            disabled={disabled}
            {...inputPropsRest}
          />

          {status === 'verified' && (
            <IconNotificationContainer>
              <IconNotificationSuccess size={'md'} />
            </IconNotificationContainer>
          )}

          {showVerifyButton && (
            <VerifyButton variant="secondary" disabled={!enableVerify} onClick={toggleVerifyModal} {...buttonPropsRest}>
              {verifyButtonLabel}
            </VerifyButton>
          )}
        </InputContainer>

        {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}

        {showVerifyModal && (
          <VerifiableContactDetailModal
            type={type}
            value={prefixedValue}
            sendCodeOnMount={value !== codeSentTo}
            onCodeSent={handleCodeSent}
            allowSkip={failuresBeforeSkip >= 0 && failuresBeforeSkip <= failuresCount}
            onVerifyCodeFailed={handleVerifyCodeFailed}
            onCancel={toggleVerifyModal}
            onComplete={handleVerifyComplete}
            api={api}
          />
        )}
      </Container>
    );
  }
);
